import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Drawer, Form, Input, Select, Button, Row, Col, Space, TreeSelect, notification, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import {
    fetchSectorsRequest,
    fetchWaterAdministrationsRequest,
    fetchCompanyDetailRequest,
    updateCompanyRequest,
    clearCompanyDetail,
    fetchAllCompaniesRequest,
    fetchCompanyTypesRequest,
    fetchCitiesRequest,
    fetchDistrictsRequest,
    fetchCountriesRequest,
    fetchProductFamiliesRequest,
    fetchCompaniesRequest,
    fetchMainSectorsRequest,
    fetchBasinsRequest,
    fetchSubscriptionTypesRequest,
    fetchWaterAdministrationMainSectorsRequest,
    fetchWaterAdministrationSubSectorsRequest,
} from '../../../../redux/reducers/clients/clientsSlice';
import {
    Sector,
    WaterAdministration,
    CompanyUpdateData,
    ClientFormData,
    CompanyType,
    Country,
    City,
    District,
    ProductModule,
    MainSector,
    Basin,
    SubscriptionType,
    WaterAdministrationMainSector,
    WaterAdministrationSubSector
} from '../../../../redux/reducers/clients/clients.types';
import { colors } from '../../../../utils/colors/colors';
import { SafeKey } from 'rc-tree/lib/interface';
import SearchableSelect from '../../../../components/SearchableSelect/SearchableSelect';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

interface UpdateClientsDrawerProps {
    open: boolean;
    onClose: () => void;
    clientId?: string;
}

interface SelectedModuleData {
    id: number;
    name: string;
}

interface SelectedFamilyData {
    name: string;
    modules: SelectedModuleData[];
}

const UpdateClientsDrawer: React.FC<UpdateClientsDrawerProps> = ({ open, onClose, clientId }) => {
    const { t } = useTranslation('clients');
    const [form] = Form.useForm<ClientFormData>();
    const dispatch = useDispatch();


    const watchedProductFamily = Form.useWatch('productFamily', form);

    const {
        sectors: { data: sectorsData },
        mainSectors: { data: mainSectorsData },
        waterAdministrations: { data: waterAdministrationsData },
        subscriptionTypes: { data: subscriptionTypesData, loading: subscriptionTypesLoading },
        companyDetail: { data: companyData, loading: companyLoading, success: updateSuccess, error: updateError },
        allCompanies: { data: allCompaniesData },
        companyTypes: { data: companyTypesData },
        countries: { data: countriesData },
        cities: { data: citiesData },
        districts: { data: districtsData },
        productFamilies: { data: productFamiliesData },
        basins: { data: basinsData },
        waterAdministrationMainSectors: { data: waMainSectorsData, loading: waMainSectorsLoading },
        waterAdministrationSubSectors: { data: waSubSectorsData, loading: waSubSectorsLoading }
    } = useSelector((state: RootState) => state.clients);

    const [selectedMainSector, setSelectedMainSector] = useState<number | null>(null);
    const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
    const [selectedCity, setSelectedCity] = useState<number | null>(null);
    const [selectedWaterAdmin, setSelectedWaterAdmin] = useState<number | null>(null);
    const [selectedWaMainSector, setSelectedWaMainSector] = useState<number | null>(null);
    const [searchValue, setSearchValue] = useState('');
    const [expandedKeys, setExpandedKeys] = useState<SafeKey[]>([]);

    const isTurkeySelected = useMemo(() => {
        return countriesData?.find(c => c.id === selectedCountry)?.name === "Türkiye";
    }, [countriesData, selectedCountry]);

    const productFamilyTreeData = useMemo(() => {
        return (productFamiliesData ?? []).map(family => ({
            value: `family-${family.id}`,
            title: t(family.name),
            isLeaf: false,
            children: (family.productModules ?? []).map(module => ({
                value: `module-${module.id}`,
                title: t(module.name),
                isLeaf: true
            }))
        }));
    }, [productFamiliesData, t]);

    const moduleToFamilyMap = useMemo(() => {
        const map = new Map<number, { familyId: number; familyName: string }>();
        (productFamiliesData ?? []).forEach(family => {
            (family.productModules ?? []).forEach(module => {
                map.set(module.id, { familyId: family.id, familyName: family.name });
            });
        });
        return map;
    }, [productFamiliesData]);

    const allModulesMap = useMemo(() => {
        const map = new Map<number, ProductModule>();
        (productFamiliesData ?? []).forEach(family => {
            (family.productModules ?? []).forEach(module => {
                map.set(module.id, module);
            });
        });
        return map;
    }, [productFamiliesData]);

    const parsedSelectedProducts = useMemo(() => {
        if (!watchedProductFamily || moduleToFamilyMap.size === 0) {
            return { families: new Map<number, SelectedFamilyData>() };
        }
        const families = new Map<number, SelectedFamilyData>();
        watchedProductFamily.forEach(value => {
            if (String(value).startsWith('module-')) {
                const moduleId = Number(String(value).replace('module-', ''));
                const moduleInfo = allModulesMap.get(moduleId);
                const familyInfo = moduleToFamilyMap.get(moduleId);
                if (moduleInfo && familyInfo) {
                    if (!families.has(familyInfo.familyId)) {
                        families.set(familyInfo.familyId, {
                            name: familyInfo.familyName,
                            modules: []
                        });
                    }
                    families.get(familyInfo.familyId)!.modules.push({
                        id: moduleInfo.id,
                        name: moduleInfo.name
                    });
                }
            }
        });
        return { families };
    }, [watchedProductFamily, moduleToFamilyMap, allModulesMap]);

    const baseDataViewerTreeData = useMemo(() => {
        if (!allCompaniesData || parsedSelectedProducts.families.size === 0) return [];
        return (allCompaniesData?.filter(c => c.id !== clientId) ?? []).map(company => {
            const familyChildren = Array.from(parsedSelectedProducts.families.entries()).map(([familyId, familyData]) => {
                const moduleChildren = familyData.modules.map(module => ({
                    value: `module-${module.id}-company-${company.id}`,
                    title: t(module.name),
                    isLeaf: true,
                }));
                return {
                    value: `family-${familyId}-company-${company.id}`,
                    title: t(familyData.name),
                    isLeaf: false,
                    children: moduleChildren,
                };
            });
            return {
                value: `company-${company.id}`,
                title: company.name,
                isLeaf: familyChildren.length === 0,
                children: familyChildren,
            };
        });
    }, [allCompaniesData, clientId, parsedSelectedProducts, t]);

    const companyMap = useMemo(() => {
        if (!allCompaniesData) return new Map();
        return new Map(allCompaniesData.map(company => [company.id, company.name]));
    }, [allCompaniesData]);

    const customTagRender = (props: any) => {
        const { label, value, closable, onClose } = props;
        const parts = String(value).split('-company-');
        if (String(value).startsWith('module-') && parts.length === 2) {
            const companyId = parts[1];
            const companyName = companyMap.get(companyId);
            if (companyName) {
                const displayLabel = `${companyName} > ${label}`;
                return (
                    <Tag closable={closable} onClose={onClose} style={{ margin: '2px' }}>
                        {displayLabel}
                    </Tag>
                );
            }
        }

        return (
            <Tag closable={closable} onClose={onClose} style={{ margin: '2px' }}>
                {label}
            </Tag>
        );
    };

    const filterTreeData = useCallback((data: any[], searchValue: string): any[] => {
        if (!searchValue) return data;
        const filterNode = (node: any): any | null => {
            const title = node.title?.toString().toLowerCase() || '';
            const matches = title.includes(searchValue.toLowerCase());
            let filteredChildren: any[] = [];
            if (node.children) {
                filteredChildren = node.children.map(filterNode).filter(Boolean);
            }
            if (matches || filteredChildren.length > 0) {
                return { ...node, children: filteredChildren };
            }
            return null;
        };
        return data.map(filterNode).filter(Boolean);
    }, []);

    const filteredDataViewerTreeData = useMemo(() => {
        return filterTreeData(baseDataViewerTreeData, searchValue);
    }, [baseDataViewerTreeData, searchValue, filterTreeData]);


    const handleFormChange = (changedValues: Record<string, any>) => {
        if (changedValues.countryId !== undefined) {
            setSelectedCountry(changedValues.countryId);
            form.setFieldsValue({ cityId: undefined, districtId: undefined });
            setSelectedCity(null);
            if (changedValues.countryId) {
                dispatch(fetchCitiesRequest(changedValues.countryId));
            }
        }
        if (changedValues.cityId !== undefined) {
            setSelectedCity(changedValues.cityId);
            form.setFieldsValue({ districtId: undefined });
            const country = countriesData?.find(c => c.id === selectedCountry);
            if (changedValues.cityId && country?.name === "Türkiye") {
                dispatch(fetchDistrictsRequest(changedValues.cityId));
            }
        }
        if (changedValues.mainSectorId !== undefined) {
            setSelectedMainSector(changedValues.mainSectorId);
            form.setFieldsValue({ sectorId: undefined });
        }
        if (changedValues.waterAdministrationId !== undefined) {
            setSelectedWaterAdmin(changedValues.waterAdministrationId);
            form.setFieldsValue({ subscriptionTypeId: undefined });
            dispatch(fetchSubscriptionTypesRequest(changedValues.waterAdministrationId));

            form.setFieldsValue({ waterAdministrationMainSectorId: undefined, waterAdministrationSubSectorId: undefined });
            setSelectedWaMainSector(null);
            dispatch(fetchWaterAdministrationMainSectorsRequest(changedValues.waterAdministrationId));
        }

        if (changedValues.waterAdministrationMainSectorId !== undefined) {
            setSelectedWaMainSector(changedValues.waterAdministrationMainSectorId);
            form.setFieldsValue({ waterAdministrationSubSectorId: undefined });
            dispatch(fetchWaterAdministrationSubSectorsRequest(changedValues.waterAdministrationMainSectorId));
        }

    };

    const processDataViewerCompanies = (
        selectedValues: string[],
        moduleToFamilyMap: Map<number, { familyId: number; familyName: string }>
    ) => {
        const companiesMap = new Map<string, { families: Set<number>; modules: Set<number> }>();
        if (!selectedValues) return [];

        selectedValues.forEach(value => {
            const parts = value.split('-company-');
            if (parts.length !== 2) return;

            const companyId = parts[1];
            const typeAndId = parts[0];

            if (!companiesMap.has(companyId)) {
                companiesMap.set(companyId, { families: new Set(), modules: new Set() });
            }
            const companyData = companiesMap.get(companyId)!;

            if (typeAndId.startsWith('module-')) {
                const moduleId = parseInt(typeAndId.replace('module-', ''), 10);
                if (isNaN(moduleId)) return;

                companyData.modules.add(moduleId);

                const familyInfo = moduleToFamilyMap.get(moduleId);
                if (familyInfo) {
                    companyData.families.add(familyInfo.familyId);
                }
            } else if (typeAndId.startsWith('family-')) {
                const familyId = parseInt(typeAndId.replace('family-', ''), 10);
                if (!isNaN(familyId)) {
                    companyData.families.add(familyId);
                }
            }
        });

        return Array.from(companiesMap.entries()).map(([companyId, data]) => ({
            companyId,
            productFamilies: Array.from(data.families),
            productModules: Array.from(data.modules),
        }));
    };

    const handleSave = async () => {
        if (!clientId) return;
        try {
            const values = await form.validateFields();
            const { productFamily, dataViewerCompanies: viewerCompaniesValues, ...restOfValues } = values;

            const separateProductData = (
                selectedValues: string[],
                moduleToFamilyMap: Map<number, { familyId: number; familyName: string }>
            ) => {
                const familyIdSet = new Set<number>();
                const moduleIds: number[] = [];

                if (!selectedValues) return { productFamilyIds: [], productModuleIds: [] };

                selectedValues.forEach(value => {
                    if (String(value).startsWith('module-')) {
                        const moduleId = Number(String(value).replace('module-', ''));
                        if (!isNaN(moduleId)) {
                            moduleIds.push(moduleId);
                            const familyInfo = moduleToFamilyMap.get(moduleId);
                            if (familyInfo) {
                                familyIdSet.add(familyInfo.familyId);
                            }
                        }
                    } else if (String(value).startsWith('family-')) {
                        const familyId = Number(String(value).replace('family-', ''));
                        if (!isNaN(familyId)) {
                            familyIdSet.add(familyId);
                        }
                    }
                });
                return { productFamilyIds: Array.from(familyIdSet), productModuleIds: moduleIds };
            };

            const { productFamilyIds, productModuleIds } = separateProductData(productFamily, moduleToFamilyMap);
            const dataViewerCompanies = processDataViewerCompanies(viewerCompaniesValues, moduleToFamilyMap);


            const updateData: Partial<CompanyUpdateData> = {
                ...restOfValues,
                totalArea: Number(restOfValues.totalArea),
                gardenTotalArea: Number(restOfValues.gardenTotalArea),
                activeWellCount: Number(restOfValues.activeWellCount),
                passiveWellCount: Number(restOfValues.passiveWellCount),
                subscriptionTypeId: restOfValues.subscriptionTypeId,
                receivingEnvironmentBasinId: restOfValues.receivingEnvironmentBasinId,
                consumptionBasinId: restOfValues.consumptionBasinId,
                deepDischargeStatus: restOfValues.deepDischargeStatus,
                dischargeToReceivingEnvironmentStatus: restOfValues.dischargeToReceivingEnvironmentStatus,
                municipalitySewageDischargeStatus: restOfValues.municipalitySewageDischargeStatus,
                osbNetworkDischargeStatus: restOfValues.osbNetworkDischargeStatus,
                waterAdministrationMainSectorId: restOfValues.waterAdministrationMainSectorId,
                waterAdministrationSubSectorId: restOfValues.waterAdministrationSubSectorId,
                latitude: restOfValues.latitude,
                longitude: restOfValues.longitude,

                productFamilyIds,
                productModuleIds,
                dataViewerCompanies,
            };

            dispatch(updateCompanyRequest({ id: clientId, data: updateData as CompanyUpdateData }));
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    const handleClose = useCallback(() => {
        form.resetFields();
        dispatch(clearCompanyDetail());
        onClose();
    }, [form, dispatch, onClose]);

    useEffect(() => {
        if (open) {
            dispatch(fetchMainSectorsRequest());
            dispatch(fetchSectorsRequest());
            dispatch(fetchWaterAdministrationsRequest());
            dispatch(fetchAllCompaniesRequest());
            dispatch(fetchCompanyTypesRequest());
            dispatch(fetchCountriesRequest());
            dispatch(fetchProductFamiliesRequest());
            dispatch(fetchBasinsRequest());
            if (clientId) {
                dispatch(fetchCompanyDetailRequest(clientId));
            }
        }
    }, [dispatch, open, clientId]);

    useEffect(() => {
        if (companyData && open) {
            const productModuleValues = (companyData.productModules ?? []).map(pm => `module-${pm.id}`);
            const initialDataViewerValues: string[] = [];
            (companyData.dataViewerCompanies ?? []).forEach(viewer => {
                (viewer.productModules ?? []).forEach(moduleId => {
                    initialDataViewerValues.push(`module-${moduleId}-company-${viewer.companyId}`);
                });
            });

            form.setFieldsValue({
                name: companyData.name,
                email: companyData.email,
                phoneNumber: companyData.phoneNumber || '',
                isActive: companyData.isActive,
                productFamily: productModuleValues,
                companyTypeId: companyData.companyTypeId || undefined,
                dataViewerCompanies: initialDataViewerValues,
                osbAfillitonStatus: companyData.osbAfillitonStatus || false,
                waterAdministrationId: companyData.waterAdministrationId || undefined,
                subscriptionTypeId: companyData.subscriptionTypeId || undefined,
                countryId: companyData.countryId || undefined,
                cityId: companyData.cityId || undefined,
                districtId: companyData.districtId || undefined,
                address: companyData.address || '',
                mainSectorId: companyData.mainSectorId || undefined,
                sectorId: companyData.sectorId || undefined,
                activeWellCount: companyData.activeWellCount || 0,
                passiveWellCount: companyData.passiveWellCount || 0,
                totalArea: companyData.totalArea || 0,
                gardenTotalArea: companyData.gardenTotalArea || 0,
                receivingEnvironmentBasinId: companyData.receivingEnvironmentBasinId || undefined,
                consumptionBasinId: companyData.consumptionBasinId || undefined,
                deepDischargeStatus: companyData.deepDischargeStatus,
                dischargeToReceivingEnvironmentStatus: companyData.dischargeToReceivingEnvironmentStatus,
                municipalitySewageDischargeStatus: companyData.municipalitySewageDischargeStatus,
                osbNetworkDischargeStatus: companyData.osbNetworkDischargeStatus,
                waterAdministrationMainSectorId: companyData.waterAdministrationMainSectorId || undefined,
                waterAdministrationSubSectorId: companyData.waterAdministrationSubSectorId || undefined,
                latitude: companyData.latitude || undefined,
                longitude: companyData.longitude || undefined
            });

            setSelectedMainSector(companyData.mainSectorId || null);
            setSelectedCountry(companyData.countryId || null);
            setSelectedCity(companyData.cityId || null);
            if (companyData.waterAdministrationId) {
                setSelectedWaterAdmin(companyData.waterAdministrationId);
                dispatch(fetchSubscriptionTypesRequest(companyData.waterAdministrationId));
                dispatch(fetchWaterAdministrationMainSectorsRequest(companyData.waterAdministrationId));
            }

            if (companyData.waterAdministrationMainSectorId) {
                setSelectedWaMainSector(companyData.waterAdministrationMainSectorId);
                dispatch(fetchWaterAdministrationSubSectorsRequest(companyData.waterAdministrationMainSectorId));
            }

            if (companyData.countryId) {
                dispatch(fetchCitiesRequest(companyData.countryId));
            }
            const country = countriesData?.find(c => c.id === companyData.countryId);
            if (companyData.cityId && country?.name === "Türkiye") {
                dispatch(fetchDistrictsRequest(companyData.cityId));
            }
        }
    }, [companyData, open, form, dispatch, countriesData]);

    useEffect(() => {
        if (updateSuccess) {
            notification.success({ message: t('client_update_success') });
            dispatch(fetchCompaniesRequest());
            handleClose();
        }
    }, [updateSuccess, dispatch, t, handleClose]);

    useEffect(() => {
        if (updateError) {
            notification.error({ message: t(updateError) });
            dispatch(clearCompanyDetail());
        }
    }, [updateError, dispatch, t]);

    const companyTypeOptions = (companyTypesData ?? []).map((ct: CompanyType) => ({
        value: ct.id,
        label: t(ct.name)
    }));

    // Options mapping
    const basinOptions = (basinsData || []).map((basin: Basin) => ({
        value: basin.id,
        label: basin.name
    }));

    const yesNoOptions = [
        { value: true, label: t("yes") },
        { value: false, label: t("no") }
    ];
    const osbAffiliationOptions = [{ value: true, label: t("affiliated") }, { value: false, label: t("not_affiliated") }];
    const waterAdministrationOptions = (waterAdministrationsData ?? []).map((admin: WaterAdministration) => ({ value: admin.id, label: admin.name }));

    const subscriptionTypeOptions = (subscriptionTypesData ?? []).map((type: SubscriptionType) => ({
        value: type.id,
        label: type.name
    }));

    const waMainSectorOptions = (waMainSectorsData || []).map((sector: WaterAdministrationMainSector) => ({
        value: sector.id,
        label: `${sector.code} - ${sector.name}`
    }));

    const waSubSectorOptions = (waSubSectorsData || []).map((sector: WaterAdministrationSubSector) => ({
        value: sector.id,
        label: sector.subSectorCode
            ? `${sector.subSectorCode} - ${sector.name}`
            : sector.name
    }));

    const countryOptions = (countriesData ?? []).map((country: Country) => ({ value: country.id, label: country.name }));
    const cityOptions = (citiesData ?? []).map((city: City) => ({ value: city.id, label: city.name }));
    const districtOptions = (districtsData ?? []).map((district: District) => ({ value: district.id, label: district.name }));

    const mainSectorOptions = (mainSectorsData ?? []).map((sector: MainSector) => ({
        value: sector.id,
        label: `${sector.name} - ${sector.naceCode}`
    }));

    const sectorOptions = (sectorsData ?? [])
        .filter((sector: Sector) => sector.mainSectorId === selectedMainSector)
        .map((sector: Sector) => ({
            value: sector.id,
            label: `${sector.name} - ${sector.naceCode}`
        }));

    return (
        <Drawer
            title={<span style={{ color: colors.blueitNavyBlue, fontWeight: 'bold' }}>{t("clientEdit")}</span>}
            placement="right"
            size="large"
            width={500}
            onClose={handleClose}
            open={open}
            destroyOnClose
            footer={
                <Space style={{ float: 'right' }}>
                    <Button onClick={handleClose}>{t("cancel")}</Button>
                    <Button type="primary" style={{ background: colors.blueitLightBlue }} onClick={handleSave} loading={companyLoading}>
                        {t("save")}
                    </Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical" onValuesChange={handleFormChange} requiredMark={false}>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item name="name" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("company_name")}</span>} rules={[{ required: true, message: t("company_name_required") }]}>
                            <Input placeholder={t("select_company_name")} />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="email" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("email")}</span>} rules={[{ required: true, message: t("email_required") }, { type: 'email', message: t("valid_email_required") }]}>
                            <Input placeholder={t("select_email")} />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="phoneNumber" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("phone_number")}</span>} rules={[{ required: true, message: t("phone_required") }, { pattern: /^(\+90|0)?[5]\d{2}[\s\-]?\d{3}[\s\-]?\d{2}[\s\-]?\d{2}$/, message: t("valid_phone_required") }]}>
                            <Input placeholder={t("select_phone")} />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="isActive" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("status")}</span>} rules={[{ required: true, message: t("status_required") }]}>
                            <Select placeholder={t("select_status")}>
                                <Option value={true}><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: colors.blueitGreen }} />{t("active")}</div></Option>
                                <Option value={false}><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: colors.blueitRed }} />{t("passive")}</div></Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="companyTypeId" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("customer_type")}</span>} rules={[{ required: true, message: t("customer_type_required") }]}>
                            <SearchableSelect
                                placeholder={t("select_customer_type")}
                                options={companyTypeOptions}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="productFamily" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("product_family")}</span>} rules={[{ required: true, message: t("product_family_required") }]}>
                            <TreeSelect showSearch={false} style={{ width: '100%' }} placeholder={t("select_product_family")} allowClear multiple treeDefaultExpandAll treeData={productFamilyTreeData} treeCheckable showCheckedStrategy={TreeSelect.SHOW_ALL} virtual listHeight={250} maxTagCount="responsive" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="dataViewerCompanies" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("data_viewer_companies")}</span>}>
                            <TreeSelect
                                className='blueit-tree-select'
                                showSearch
                                style={{ width: '100%' }}
                                placeholder={t("select_data_viewer_companies")}
                                allowClear
                                multiple
                                treeData={baseDataViewerTreeData}
                                treeCheckable
                                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                                virtual
                                listHeight={300}
                                treeExpandedKeys={expandedKeys}
                                onTreeExpand={(keys: SafeKey[]) => setExpandedKeys(keys)}
                                maxTagCount="responsive"
                                filterTreeNode={(inputValue, treeNode) => {
                                    const title = String(treeNode.title || '').toLowerCase();
                                    return title.includes(inputValue.toLowerCase());
                                }}
                                onSearch={setSearchValue}
                                onChange={() => setSearchValue('')}
                                notFoundContent={
                                    watchedProductFamily && watchedProductFamily.length > 0
                                        ? t("no_data_found")
                                        : t("select_product_family_first")
                                }
                                tagRender={customTagRender}
                                disabled={!watchedProductFamily || watchedProductFamily.length === 0}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="osbAfillitonStatus" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("osb_affiliation_status")}</span>}>
                            <Select
                                placeholder={t("select_osb_status")}
                                options={osbAffiliationOptions}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="waterAdministrationId" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("water_administration")}</span>} rules={[{ required: true, message: t("water_administration_required") }]}>
                            <SearchableSelect
                                placeholder={t("select_water_administration")}
                                options={waterAdministrationOptions}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="subscriptionTypeId" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("subscription_type")}</span>}>
                            <SearchableSelect
                                placeholder={t("select_subscription_type")}
                                options={subscriptionTypeOptions}
                                disabled={!selectedWaterAdmin}
                                loading={subscriptionTypesLoading}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="waterAdministrationMainSectorId"
                            label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("water_admin_main_sector") || "İdare Ana Sektör (KÖP)"}</span>}
                        >
                            <SearchableSelect
                                placeholder={t("select") || "Seçiniz"}
                                options={waMainSectorOptions}
                                disabled={!selectedWaterAdmin}
                                loading={waMainSectorsLoading}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="waterAdministrationSubSectorId"
                            label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("water_admin_sub_sector") || "İdare Alt Sektör (KÖP)"}</span>}
                        >
                            <SearchableSelect
                                placeholder={t("select") || "Seçiniz"}
                                options={waSubSectorOptions}
                                disabled={!selectedWaMainSector}
                                loading={waSubSectorsLoading}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="countryId" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("company_country")}</span>} rules={[{ required: true, message: t("country_required") }]}>
                            <SearchableSelect
                                placeholder={t("select_country")}
                                options={countryOptions}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="cityId" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("company_city")}</span>} rules={[{ required: true, message: t("city_required") }]}>
                            <SearchableSelect
                                placeholder={t("select_city")}
                                disabled={!selectedCountry}
                                options={cityOptions}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="districtId" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("district")}</span>} rules={isTurkeySelected ? [{ required: true, message: t("district_required") }] : []}>
                            <SearchableSelect
                                placeholder={t("select_district")}
                                disabled={!selectedCity || !isTurkeySelected}
                                options={districtOptions}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="address" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("company_address")}</span>} rules={[{ required: true, message: t("address_required") }]}>
                            <TextArea rows={2} placeholder={t("enter_address")} />
                        </Form.Item>
                    </Col>
                    <Col xs={12}>
                        <Form.Item label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("latitude")}</span>} name="latitude">
                            <Input type="number" placeholder={t("enter_latitude")} />
                        </Form.Item>
                    </Col>
                    <Col xs={12}>
                        <Form.Item label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("longitude")}</span>} name="longitude">
                            <Input type="number" placeholder={t("enter_longitude")} />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="mainSectorId" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("main_sector")}</span>} rules={[{ required: true, message: t("main_sector_required") }]}>
                            <SearchableSelect
                                placeholder={t("select_main_sector")}
                                options={mainSectorOptions}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="sectorId" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("secondary_sector")}</span>} rules={[{ required: true, message: t("secondary_sector_required") }]}>
                            <SearchableSelect
                                allowClear
                                placeholder={t("select_secondary_sector")}
                                options={sectorOptions}
                                disabled={!selectedMainSector} // Disable if no main sector is selected
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={12}>
                        <Form.Item name="activeWellCount" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("active_well_count")}</span>} rules={[{ required: true, message: t("active_well_count_required") }]}>
                            <Input type="number" placeholder={t("enter_active_well_count")} />
                        </Form.Item>
                    </Col>
                    <Col xs={12}>
                        <Form.Item name="passiveWellCount" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("passive_well_count")}</span>} rules={[{ required: true, message: t("passive_well_count_required") }]}>
                            <Input type="number" placeholder={t("enter_passive_well_count")} />
                        </Form.Item>
                    </Col>
                    <Col xs={12}>
                        <Form.Item name="totalArea" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("total_area")}</span>} rules={[{ required: true, message: t("total_area_required") }]}>
                            <Input type="number" placeholder={t("enter_total_area")} />
                        </Form.Item>
                    </Col>
                    <Col xs={12}>
                        <Form.Item name="gardenTotalArea" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("garden_total_area")}</span>} rules={[{ required: true, message: t("garden_area_required") }]}>
                            <Input type="number" placeholder={t("enter_garden_area")} />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="receivingEnvironmentBasinId" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("receiving_environment_basin") || "Alıcı Ortam Havzası"}</span>} rules={[{ required: true, message: t("required_field") }]}>
                            <SearchableSelect
                                placeholder={t("select_basin") || "Havza Seçiniz"}
                                options={basinOptions}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="consumptionBasinId" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("consumption_basin") || "Tüketim Havzası"}</span>} rules={[{ required: true, message: t("required_field") }]}>
                            <SearchableSelect
                                placeholder={t("select_basin") || "Havza Seçiniz"}
                                options={basinOptions}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="deepDischargeStatus" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("deep_discharge_status") || "Derin deşarj durumu?"}</span>} rules={[{ required: true, message: t("required_field") }]}>
                            <Select placeholder={t("select") || "Seçiniz"} options={yesNoOptions} />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="dischargeToReceivingEnvironmentStatus" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("discharge_to_receiving_environment_status") || "Alıcı Ortama Deşarj Durumu?"}</span>} rules={[{ required: true, message: t("required_field") }]}>
                            <Select placeholder={t("select") || "Seçiniz"} options={yesNoOptions} />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="municipalitySewageDischargeStatus" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("municipality_sewage_discharge_status") || "Belediye Kanalizasyon Şebekesi Deşarj durumu?"}</span>} rules={[{ required: true, message: t("required_field") }]}>
                            <Select placeholder={t("select") || "Seçiniz"} options={yesNoOptions} />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="osbNetworkDischargeStatus" label={<span style={{ color: colors.blueitNavyBlue, fontWeight: '500' }}>{t("osb_network_discharge_status") || "OSB Şebekesi deşarj var mı?"}</span>} rules={[{ required: true, message: t("required_field") }]}>
                            <Select placeholder={t("select") || "Seçiniz"} options={yesNoOptions} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    );
};

export default UpdateClientsDrawer;